import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import api from "@/lib/api";

const ICON_BY_TYPE = {
  project_assigned: "📋",
  project_rejected: "⚠️",
  info_requested: "❓",
  info_received: "📨",
  survey_assigned: "🧭",
  new_request: "🆕",
  new_survey: "🆕",
  quote_ready: "🧾",
};

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/**
 * Notifications bell with unread badge + dropdown. Polls every 60s.
 * Used in Layout topbar / sidebar for every authenticated role.
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); } catch { /* ignore */ }
    await load();
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); } catch { /* ignore */ }
    await load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-white/10 transition-colors"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--bq-orange)] text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notification-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-80 max-h-[480px] bg-white text-[var(--bq-text)] border border-[var(--bq-border)] rounded-xl shadow-2xl overflow-hidden z-50"
          data-testid="notification-dropdown"
        >
          <div className="px-4 py-3 border-b border-[var(--bq-border)] flex items-center justify-between">
            <span className="font-display font-bold text-sm">Notifications</span>
            {unread > 0 && (
              <button
                onClick={markAll}
                className="text-xs inline-flex items-center gap-1 hover:text-[var(--bq-orange)]"
                data-testid="notification-mark-all"
              >
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="overflow-y-auto max-h-[420px]">
            {items.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-[var(--bq-text-muted)]">
                You're all caught up.
              </div>
            )}
            {items.map((n) => (
              <Link
                key={n.id}
                to={n.project_id ? `/projects/${n.project_id}` : "/dashboard"}
                onClick={() => { if (!n.read) markRead(n.id); setOpen(false); }}
                className={`block px-4 py-3 hover:bg-[var(--bq-bg)] border-b border-[var(--bq-border)] last:border-b-0 ${
                  n.read ? "opacity-60" : ""
                }`}
                data-testid={`notification-item-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  <span className="text-base leading-none mt-0.5">
                    {ICON_BY_TYPE[n.type] || "🔔"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-sm truncate">{n.title}</span>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-[var(--bq-orange)] shrink-0" />}
                    </div>
                    {n.body && <p className="text-xs text-[var(--bq-text-muted)] line-clamp-2">{n.body}</p>}
                    <span className="text-[10px] text-[var(--bq-text-muted)]">{timeAgo(n.created_at)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
